import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { listOrganizationEmployees, type CoreEmployee, type CoreOrganization } from '@/lib/zifekCore';
import {
  CALENDAR_EVENT_TYPES,
  calendarEventTypeMeta,
  createCoreCalendarEvent,
  deleteCoreCalendarEvent,
  EVENT_STATUSES,
  listCoreCalendarEvents,
  listCoreCalendars,
  type CoreCalendar,
  type CoreCalendarEvent,
} from '@/lib/coreSocle';
import Modal from '@/pages/dashboard/core/components/Modal';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

interface UnifiedItem {
  id: string;
  source: 'event' | 'appointment';
  title: string;
  typeKey: string;
  when: Date;
  location: string | null;
  statusLabel: string;
  statusKey: string;
  employeeId: number | null;
  raw: CoreCalendarEvent | null;
}

interface ApptRow {
  id: number;
  service_id: number;
  fullname: string;
  phone: string | null;
  date: string;
  time: string;
  status: string | null;
}

interface FormState {
  title: string;
  eventType: string;
  date: string;
  time: string;
  endTime: string;
  location: string;
  employeeId: number | null;
  status: string;
  description: string;
}

function pad(n: number): string { return String(n).padStart(2, '0'); }

function defaultForm(): FormState {
  const now = new Date();
  return {
    title: '', eventType: 'event',
    date: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
    time: `${pad(now.getHours())}:00`, endTime: '', location: '', employeeId: null,
    status: 'planned', description: '',
  };
}

export default function CalendarTab({ commerceId, org }: Props) {
  const [calendars, setCalendars] = useState<CoreCalendar[]>([]);
  const [events, setEvents] = useState<CoreCalendarEvent[]>([]);
  const [appts, setAppts] = useState<ApptRow[]>([]);
  const [employees, setEmployees] = useState<CoreEmployee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(defaultForm());
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [view, setView] = useState<'list' | 'month'>('list');
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { year: d.getFullYear(), month: d.getMonth() }; });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [cals, evs, emps] = await Promise.all([
        listCoreCalendars(commerceId),
        listCoreCalendarEvents(org.id),
        listOrganizationEmployees(org.id),
      ]);
      setCalendars(cals);
      setEvents(evs);
      setEmployees(emps.filter((e) => e.status !== 'inactive'));

      const serviceIds = cals.map((c) => c.id);
      if (serviceIds.length > 0) {
        const { data } = await supabase
          .from('booking_appointments')
          .select('id, service_id, fullname, phone, date, time, status')
          .in('service_id', serviceIds)
          .order('date', { ascending: true });
        setAppts((data as ApptRow[]) || []);
      } else {
        setAppts([]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [commerceId, org.id]);

  useEffect(() => { load(); }, [load]);

  const items = useMemo<UnifiedItem[]>(() => {
    const list: UnifiedItem[] = [];
    events.forEach((e) => {
      list.push({
        id: `e${e.id}`, source: 'event', title: e.title,
        typeKey: e.event_type || 'event', when: new Date(e.start_at),
        location: e.location, statusLabel: EVENT_STATUSES.find((s) => s.key === e.status)?.label || 'Planifié',
        statusKey: e.status || 'planned', employeeId: e.employee_id, raw: e,
      });
    });
    const calName = (id: number) => calendars.find((c) => c.id === id)?.service_name || 'Rendez-vous';
    appts.forEach((a) => {
      list.push({
        id: `a${a.id}`, source: 'appointment', title: `${calName(a.service_id)} — ${a.fullname}`,
        typeKey: 'appointment', when: new Date(`${a.date}T${a.time || '00:00'}`),
        location: null, statusLabel: a.status || 'Réservé', statusKey: 'confirmed',
        employeeId: null, raw: null,
      });
    });
    const valid = list.filter((i) => !Number.isNaN(i.when.getTime()));
    valid.sort((a, b) => a.when.getTime() - b.when.getTime());
    return valid;
  }, [events, appts, calendars]);

  const filtered = typeFilter === 'all' ? items : items.filter((i) => i.typeKey === typeFilter);

  const grouped = useMemo(() => {
    const map = new Map<string, UnifiedItem[]>();
    filtered.forEach((i) => {
      const key = i.when.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(i);
    });
    return Array.from(map.entries());
  }, [filtered]);

  const empLabel = (id: number | null) => (id ? (employees.find((e) => e.id === id)?.job_title || `Employé #${id}`) : null);

  const monthLabel = new Date(cursor.year, cursor.month, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  const monthDays = useMemo(() => {
    const first = new Date(cursor.year, cursor.month, 1);
    const startOffset = (first.getDay() + 6) % 7; // lundi = 0
    const daysInMonth = new Date(cursor.year, cursor.month + 1, 0).getDate();
    const cells: (number | null)[] = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor]);

  const itemsByDay = useMemo(() => {
    const map = new Map<number, UnifiedItem[]>();
    filtered.forEach((i) => {
      if (i.when.getFullYear() === cursor.year && i.when.getMonth() === cursor.month) {
        const d = i.when.getDate();
        if (!map.has(d)) map.set(d, []);
        map.get(d)!.push(i);
      }
    });
    return map;
  }, [filtered, cursor]);

  const isToday = (day: number) => {
    const now = new Date();
    return now.getFullYear() === cursor.year && now.getMonth() === cursor.month && now.getDate() === day;
  };

  const openDay = (day: number) => {
    setForm({ ...defaultForm(), date: `${cursor.year}-${pad(cursor.month + 1)}-${pad(day)}` });
    setOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const startAt = new Date(`${form.date}T${form.time || '00:00'}`).toISOString();
      const endAt = form.endTime ? new Date(`${form.date}T${form.endTime}`).toISOString() : null;
      await createCoreCalendarEvent({
        tenantId: org.tenant_id,
        organizationId: org.id,
        title: form.title.trim(),
        eventType: form.eventType,
        startAt,
        endAt,
        location: form.location.trim() || null,
        employeeId: form.employeeId,
        status: form.status,
        description: form.description.trim() || null,
      });
      setOpen(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item: UnifiedItem) => {
    if (item.source !== 'event' || !item.raw) return;
    setBusyId(item.id);
    try {
      await deleteCoreCalendarEvent(item.raw.id);
      load();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
          <button onClick={() => setTypeFilter('all')} className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${typeFilter === 'all' ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>Tout</button>
          {CALENDAR_EVENT_TYPES.map((t) => (
            <button key={t.key} onClick={() => setTypeFilter(t.key)} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${typeFilter === t.key ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>{t.label}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
            <button onClick={() => setView('list')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${view === 'list' ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}><i className="ri-list-check"></i>Liste</button>
            <button onClick={() => setView('month')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${view === 'month' ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}><i className="ri-calendar-2-line"></i>Mois</button>
          </div>
          <button onClick={() => { setForm(defaultForm()); setOpen(true); }} className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">
            <i className="ri-calendar-event-line"></i> Nouvel événement
          </button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-3 text-xs text-foreground-500">
        <span className="inline-flex items-center gap-1.5"><i className="ri-calendar-2-line text-secondary-600"></i>{calendars.length} calendrier{calendars.length > 1 ? 's' : ''} de réservation</span>
        <span className="inline-flex items-center gap-1.5"><i className="ri-calendar-check-line text-primary-600"></i>{appts.length} rendez-vous</span>
        <span className="inline-flex items-center gap-1.5"><i className="ri-calendar-event-line text-accent-600"></i>{events.length} événement{events.length > 1 ? 's' : ''} interne{events.length > 1 ? 's' : ''}</span>
      </div>

      {error && <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : view === 'month' ? (
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-3 md:p-4">
          <div className="flex items-center justify-between mb-3">
            <button onClick={() => setCursor((c) => { const d = new Date(c.year, c.month - 1, 1); return { year: d.getFullYear(), month: d.getMonth() }; })} className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200/70 transition-colors cursor-pointer"><i className="ri-arrow-left-s-line"></i></button>
            <p className="text-sm font-bold font-heading text-foreground-900 capitalize">{monthLabel}</p>
            <button onClick={() => setCursor((c) => { const d = new Date(c.year, c.month + 1, 1); return { year: d.getFullYear(), month: d.getMonth() }; })} className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200/70 transition-colors cursor-pointer"><i className="ri-arrow-right-s-line"></i></button>
          </div>
          <div className="grid grid-cols-7 gap-1 mb-1">
            {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((d) => (
              <div key={d} className="text-center text-[10px] font-semibold text-foreground-400 uppercase tracking-wider py-1">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {monthDays.map((day, idx) => {
              if (day === null) return <div key={`e${idx}`} className="min-h-[84px] rounded-md bg-background-100/40"></div>;
              const dayItems = itemsByDay.get(day) || [];
              const today = isToday(day);
              return (
                <button
                  key={day}
                  onClick={() => openDay(day)}
                  className={`text-left min-h-[84px] rounded-md border p-1.5 transition-colors cursor-pointer ${today ? 'border-primary-300 bg-primary-50/40' : 'border-background-200/60 bg-background-50 hover:bg-background-100'}`}
                >
                  <span className={`text-[11px] font-semibold ${today ? 'text-primary-700' : 'text-foreground-600'}`}>{day}</span>
                  <div className="mt-1 space-y-0.5">
                    {dayItems.slice(0, 2).map((i) => {
                      const tm = calendarEventTypeMeta(i.typeKey);
                      return (
                        <div key={i.id} className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium truncate ${tm.className}`}>
                          <i className={`${tm.icon} flex-shrink-0`}></i>
                          <span className="truncate">{i.title}</span>
                        </div>
                      );
                    })}
                    {dayItems.length > 2 && (
                      <span className="text-[10px] text-foreground-400 pl-1">+{dayItems.length - 2} autre{dayItems.length - 2 > 1 ? 's' : ''}</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ) : grouped.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3"><i className="ri-calendar-line text-2xl text-foreground-400"></i></div>
          <p className="text-foreground-600">Aucun événement pour ce filtre.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {grouped.map(([day, dayItems]) => (
            <div key={day}>
              <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">{day}</p>
              <div className="space-y-2">
                {dayItems.map((i) => {
                  const tm = calendarEventTypeMeta(i.typeKey);
                  const timeLabel = i.when.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
                  const emp = empLabel(i.employeeId);
                  return (
                    <div key={i.id} className="bg-background-50 border border-background-200/70 rounded-lg p-4 flex flex-wrap items-center gap-3">
                      <div className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 bg-background-100">
                        <i className={`${tm.icon} text-lg`}></i>
                      </div>
                      <div className="flex items-center gap-2 text-foreground-900 min-w-0 flex-1">
                        <span className="text-sm font-bold tabular-nums whitespace-nowrap">{timeLabel}</span>
                        <span className="text-sm font-medium truncate">{i.title}</span>
                      </div>
                      <span className={`text-[10px] px-2.5 py-1 rounded-full font-medium whitespace-nowrap ${tm.className}`}>{tm.label}</span>
                      {emp && <span className="text-[10px] px-2 py-1 rounded-full bg-secondary-100 text-secondary-900 whitespace-nowrap hidden sm:inline-flex items-center gap-1"><i className="ri-user-star-line"></i>{emp}</span>}
                      <span className="text-[10px] px-2 py-1 rounded-full bg-background-100 text-foreground-500 whitespace-nowrap">{i.statusLabel}</span>
                      {i.source === 'event' && (
                        <button onClick={() => remove(i)} disabled={busyId === i.id} className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50"><i className="ri-delete-bin-line"></i></button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <Modal
          title="Nouvel événement"
          onClose={() => setOpen(false)}
          maxWidth="max-w-xl"
          footer={
            <>
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={save} disabled={saving || !form.title.trim()} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>} Enregistrer
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Titre</span>
              <input className={INPUT} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex : Réunion équipe" autoFocus />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Type</span>
                <select className={INPUT} value={form.eventType} onChange={(e) => setForm({ ...form, eventType: e.target.value })}>
                  {CALENDAR_EVENT_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Statut</span>
                <select className={INPUT} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  {EVENT_STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </label>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Date</span>
                <input type="date" className={INPUT} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Début</span>
                <input type="time" className={INPUT} value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Fin</span>
                <input type="time" className={INPUT} value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} />
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Lieu</span>
                <input className={INPUT} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Ex : Bureau" />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Employé</span>
                <select className={INPUT} value={form.employeeId ?? ''} onChange={(e) => setForm({ ...form, employeeId: e.target.value ? Number(e.target.value) : null })}>
                  <option value="">Aucun</option>
                  {employees.map((e) => <option key={e.id} value={e.id}>{e.job_title || e.email || `Employé #${e.id}`}</option>)}
                </select>
              </label>
            </div>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Description</span>
              <textarea className={`${INPUT} min-h-[70px]`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </label>
          </div>
        </Modal>
      )}
    </div>
  );
}